import { useState } from 'react';
import { Check } from 'lucide-react';
import Button from '../../../components/ui/Button';
import type { ActivityStep } from '../classroomApi';

interface ChoiceResponseProps {
  step: ActivityStep;
  disabled?: boolean;
  submitted?: boolean;
  /** Choice indices the SERVER has for this student on this step (from the
   *  shared live poll). Authoritative: a reload, a device switch or a
   *  remount mid-step all still show the student what they actually chose. */
  recorded?: number[];
  onSubmit: (payload: { choice: number } | { choices: number[] }) => void;
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** The label the room uses out loud ("hands up if you picked B") and the one
 *  the projector prints. Beyond Z — no authored step has more than 12
 *  options — it falls back to the 1-based number. */
export function optionLetter(index: number): string {
  return index < LETTERS.length ? LETTERS[index] : String(index + 1);
}

// /class live redesign (2026-09-11). What changed and why, all of it driven
// by "a 390 px phone in a live class is the primary device":
//
// - **Full-width rows, 56 px tall, one per line.** The old control was a
//   wrapping row of text-sm chips ~36 px high — under the 44 px minimum this
//   feature already holds ConfidenceButtons to, and ragged: options of
//   different lengths wrapped into a shape that had to be READ rather than
//   scanned. The catalogue's real shape settles the layout: 181 of 187
//   authored choice steps have 2-5 options, so a stacked list costs nothing
//   and is unmissable. Only the two steps with more than 6 options fall back
//   to a two-column grid, where a 12-item stack would otherwise be a scroll.
// - **A letter badge per option**, aria-hidden so the button's accessible
//   name stays exactly the option text. It is what the instructor says out
//   loud and what the projector shows; matching them is free here.
// - **Selection comes from the server, not only from local state.** `picked`
//   still drives the instant highlight on tap (a poll must feel immediate),
//   but it re-syncs whenever the recorded answer changes, so the highlight
//   survives a reload and can never disagree with the receipt underneath.
export default function ChoiceResponse({ step, disabled, submitted, recorded, onSubmit }: ChoiceResponseProps) {
  const options = step.options || [];
  const multi = !!step.multi;

  const recordedKey = (recorded ?? []).join(',');
  const [picked, setPicked] = useState<number[]>(recorded ?? []);
  const [syncedKey, setSyncedKey] = useState<string>(recordedKey);

  // React's documented "adjust state while rendering" pattern (the same one
  // ActivityTabs uses, and for the same reason): the condition that triggers
  // the update is made false by the update itself, so it converges in this
  // render pass instead of leaving a tick where the highlight disagrees with
  // the server. A plain effect would flash the wrong selection first.
  if (recordedKey !== syncedKey) {
    setSyncedKey(recordedKey);
    setPicked(recorded ?? []);
  }

  const toggle = (idx: number) => {
    if (disabled) return;
    if (!multi) {
      setPicked([idx]);
      onSubmit({ choice: idx });
      return;
    }
    setPicked((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]));
  };

  return (
    <div className="space-y-3">
      <div className={`grid gap-2 ${options.length > 6 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {options.map((opt, idx) => {
          const isPicked = picked.includes(idx);
          return (
            <button
              key={idx}
              type="button"
              disabled={disabled}
              onClick={() => toggle(idx)}
              aria-pressed={isPicked}
              className={`flex min-h-[56px] w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left text-base font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent disabled:cursor-not-allowed disabled:opacity-60 ${
                isPicked
                  ? 'border-ws-accent bg-ws-accent-quiet text-ws-accent-ink'
                  : 'border-ws-border bg-ws-surface text-ws-ink hover:bg-ws-surface-2'
              }`}
            >
              <span
                aria-hidden="true"
                className={`grid size-8 shrink-0 place-items-center rounded-md border text-sm font-bold ${
                  isPicked
                    ? 'border-ws-accent bg-ws-accent text-ws-on-accent'
                    : 'border-ws-border bg-ws-surface-2 text-ws-ink-2'
                }`}
              >
                {optionLetter(idx)}
              </span>
              <span className="min-w-0 flex-1 break-words">{opt}</span>
              {isPicked && <Check size={18} className="shrink-0" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      {multi && (
        <Button
          size="lg"
          className="w-full sm:w-auto"
          disabled={disabled || picked.length === 0}
          onClick={() => onSubmit({ choices: picked })}
        >
          {submitted ? 'Update answer' : 'Submit'}
        </Button>
      )}
    </div>
  );
}
